import { useMemo, useRef, useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, Upload, ArrowLeft, ArrowRight, Loader2, CheckCircle2, AlertCircle, FileSpreadsheet, Search, Info, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { apiList, apiPost } from "@/integrations/api/client";
import {
  IMPORT_COLUMN_SPECS, downloadTemplate, downloadMasterData, parseCsvFile, validateRows,
  downloadErrorReport, downloadCodesList,
  type ImportRow, type Lookups, type ValidatedRow, type ColumnSpec,
} from "@/features/assets/importCsv";

export const Route = createFileRoute("/_authenticated/assets/import")({
  component: ImportPage,
});

const ALLOWED = new Set(["super_admin", "biomed_head", "facility_manager"]);

function ImportPage() {
  const navigate = useNavigate();
  const { data: me, isLoading: meLoading } = useCurrentUser();
  const canImport = (me?.roles ?? []).some((r) => ALLOWED.has(r));

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [, setRows] = useState<ImportRow[]>([]);
  const [validated, setValidated] = useState<ValidatedRow[]>([]);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState<{ imported: string[]; failed: number } | null>(null);
  const [lookups, setLookups] = useState<Lookups | null>(null);
  const [loadingLookups, setLoadingLookups] = useState(false);

  // Column instruction filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [groupFilter, setGroupFilter] = useState<string>("all");

  const fileInputRef = useRef<HTMLInputElement>(null);

  const validRows = useMemo(() => validated.filter((v) => v.errors.length === 0), [validated]);
  const errorRows = useMemo(() => validated.filter((v) => v.errors.length > 0), [validated]);

  // Load system master data lookups on mount
  useEffect(() => {
    async function fetchLookups() {
      setLoadingLookups(true);
      try {
        const [cats, locs, depts, vens, users, stores] = await Promise.all([
          apiList<{ id: string; name: string; parent_id: string | null }>("/api/v1/masters/asset-categories/", { limit: 1000 }),
          apiList<{ id: string; name: string; branch_id: string }>("/api/v1/locations/locations/", { limit: 1000 }),
          apiList<{ id: string; name: string }>("/api/v1/accounts/departments/", { limit: 1000 }),
          apiList<{ id: string; name: string }>("/api/v1/masters/vendors/", { limit: 1000 }),
          apiList<{ id: string; email: string }>("/api/v1/accounts/admin-users/", { limit: 1000 }),
          apiList<{ id: string; name: string; branch_id: string }>("/api/v1/locations/stores/", { limit: 1000 }),
        ]);

        setLookups({
          categories: cats.results ?? [],
          locations: locs.results ?? [],
          departments: depts.results ?? [],
          vendors: vens.results ?? [],
          users: users.results ?? [],
          stores: stores.results ?? [],
        });
      } catch (e) {
        console.error("Failed to load master lookups", e);
      } finally {
        setLoadingLookups(false);
      }
    }
    fetchLookups();
  }, []);

  async function handleFile(file: File) {
    setParsing(true);
    try {
      const parsed = await parseCsvFile(file);
      setRows(parsed);

      let currentLookups = lookups;
      if (!currentLookups) {
        const [cats, locs, depts, vens, users, stores] = await Promise.all([
          apiList<{ id: string; name: string; parent_id: string | null }>("/api/v1/masters/asset-categories/", { limit: 1000 }),
          apiList<{ id: string; name: string; branch_id: string }>("/api/v1/locations/locations/", { limit: 1000 }),
          apiList<{ id: string; name: string }>("/api/v1/accounts/departments/", { limit: 1000 }),
          apiList<{ id: string; name: string }>("/api/v1/masters/vendors/", { limit: 1000 }),
          apiList<{ id: string; email: string }>("/api/v1/accounts/admin-users/", { limit: 1000 }),
          apiList<{ id: string; name: string; branch_id: string }>("/api/v1/locations/stores/", { limit: 1000 }),
        ]);

        currentLookups = {
          categories: cats.results ?? [],
          locations: locs.results ?? [],
          departments: depts.results ?? [],
          vendors: vens.results ?? [],
          users: users.results ?? [],
          stores: stores.results ?? [],
        };
        setLookups(currentLookups);
      }

      const serialsRes = await apiList<{ serial_number: string }>("/api/v1/assets/", { limit: 10000, ordering: "-created_at" });
      const existing = new Set(
        (serialsRes.results ?? [])
          .map((r) => (r.serial_number ? String(r.serial_number).toLowerCase() : null))
          .filter((s): s is string => !!s)
      );

      setValidated(validateRows(parsed, currentLookups, existing));
      setStep(2);
    } catch (e: unknown) {
      toast.error((e as Error).message || "Failed to parse file");
    } finally {
      setParsing(false);
    }
  }

  async function runImport() {
    setImporting(true);
    const codes: string[] = [];
    let failed = 0;
    for (const v of validRows) {
      try {
        const created = await apiPost<{ asset_code?: string }>("/api/v1/assets/", v.payload);
        if (created?.asset_code) codes.push(created.asset_code);
      } catch (e) {
        console.error("Import failed for row", v.rowIndex, e);
        failed++;
      }
    }
    setSummary({ imported: codes, failed });
    setImporting(false);
    toast.success(`Imported ${codes.length} assets`);
  }

  // Filtered column specs for instruction guide
  const filteredSpecs = useMemo(() => {
    return IMPORT_COLUMN_SPECS.filter((col) => {
      const matchesSearch =
        searchQuery.trim() === "" ||
        col.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        col.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
        col.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (col.options && col.options.some((o) => o.toLowerCase().includes(searchQuery.toLowerCase())));

      if (!matchesSearch) return false;
      if (groupFilter === "all") return true;
      if (groupFilter === "required") return col.required === "required";
      if (groupFilter === "conditional") return col.required === "conditional";
      return col.group === groupFilter;
    });
  }, [searchQuery, groupFilter]);

  if (meLoading) return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!canImport) return (
    <Card className="p-8 text-center">
      <AlertCircle className="mx-auto h-8 w-8 text-amber-500" />
      <h2 className="mt-3 text-lg font-semibold">Access denied</h2>
      <p className="text-sm text-muted-foreground">You don't have permission to import assets.</p>
    </Card>
  );

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-10">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Bulk Asset Import</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Download standardized Excel/CSV templates with predefined dropdown choices and import serialized assets in bulk.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate({ to: "/assets" })}>
          <ArrowLeft className="mr-1.5 h-4 w-4" /> Back to Asset Registry
        </Button>
      </div>

      {/* Stepper Header */}
      <ol className="flex flex-wrap items-center gap-3 text-sm">
        {(["1. Download Template & Guidelines", "2. Upload & Validate File", "3. Review & Confirm Import"] as const).map((label, i) => {
          const n = (i + 1) as 1 | 2 | 3;
          const isActive = step === n;
          const isDone = step > n;
          return (
            <li
              key={label}
              className={`flex items-center gap-2.5 rounded-lg border px-4 py-2 font-medium transition-all ${
                isActive
                  ? "border-primary bg-primary/10 text-primary shadow-sm"
                  : isDone
                  ? "border-emerald-500/30 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/20 dark:text-emerald-300"
                  : "text-muted-foreground bg-card"
              }`}
            >
              <span className={`grid h-6 w-6 place-items-center rounded-full text-xs font-semibold ${
                isActive ? "bg-primary text-primary-foreground" : isDone ? "bg-emerald-600 text-white" : "bg-muted text-muted-foreground"
              }`}>
                {isDone ? <CheckCircle2 className="h-3.5 w-3.5" /> : n}
              </span>
              {label}
            </li>
          );
        })}
      </ol>

      {/* Step 1 — Download Template & Instructions */}
      {step === 1 && (
        <div className="space-y-6">
          {/* Downloads Card */}
          <Card className="border-l-4 border-l-primary shadow-sm">
            <CardContent className="p-6 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <FileSpreadsheet className="h-5 w-5 text-primary" />
                    Download Standardized Import Templates
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Templates include formatted Title-Case headers, predefined dropdown options, and live system master data reference sheets.
                  </p>
                </div>
                <Button size="lg" className="shrink-0" onClick={() => setStep(2)}>
                  Proceed to Upload <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <Button variant="default" onClick={() => downloadTemplate("xlsx", lookups || undefined)}>
                  <Download className="mr-2 h-4 w-4" /> Download Excel Template (.xlsx)
                </Button>

                <Button variant="outline" onClick={() => downloadTemplate("csv", lookups || undefined)}>
                  <Download className="mr-2 h-4 w-4" /> Download CSV Template (.csv)
                </Button>

                {lookups && (
                  <Button variant="secondary" onClick={() => downloadMasterData(lookups)}>
                    <FileSpreadsheet className="mr-2 h-4 w-4" /> Export Master Lookups (.xlsx)
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* System Master Data Live Summary */}
          {lookups && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
              {[
                {
                  label: "Parent Categories",
                  count: lookups.categories.filter((c) => !c.parent_id).length,
                  color: "text-blue-600 bg-blue-50 dark:bg-blue-950/40",
                },
                { label: "Locations", count: lookups.locations.length, color: "text-indigo-600 bg-indigo-50 dark:bg-indigo-950/40" },
                { label: "Departments", count: lookups.departments.length, color: "text-purple-600 bg-purple-50 dark:bg-purple-950/40" },
                { label: "Stores", count: lookups.stores.length, color: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40" },
                { label: "Vendors", count: lookups.vendors.length, color: "text-amber-600 bg-amber-50 dark:bg-amber-950/40" },
                { label: "Active Users", count: lookups.users.length, color: "text-rose-600 bg-rose-50 dark:bg-rose-950/40" },
              ].map((m) => (
                <Card key={m.label} className="p-3 text-center border shadow-2xs">
                  <div className={`text-xl font-bold ${m.color.split(" ")[0]}`}>{m.count}</div>
                  <div className="text-xs font-medium text-foreground mt-0.5">{m.label} Available</div>
                </Card>
              ))}
            </div>
          )}

          {/* Column Instruction Guide */}
          <Card className="shadow-xs">
            <CardContent className="p-6 space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-base font-semibold flex items-center gap-2">
                    <Info className="h-4 w-4 text-blue-600" /> Column Instructions &amp; Predefined Dropdown Guide
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Review required fields, predefined choice options, and validation rules for all 26 import columns.
                  </p>
                </div>

                {/* Filter and Search Bar */}
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search column or dropdown..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 h-9 text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Group Filter Tabs */}
              <Tabs value={groupFilter} onValueChange={setGroupFilter} className="w-full">
                <TabsList className="grid grid-cols-5 w-full max-w-2xl text-xs h-9">
                  <TabsTrigger value="all">All ({IMPORT_COLUMN_SPECS.length})</TabsTrigger>
                  <TabsTrigger value="required">Required ({IMPORT_COLUMN_SPECS.filter(c => c.required === "required").length})</TabsTrigger>
                  <TabsTrigger value="conditional">Conditional ({IMPORT_COLUMN_SPECS.filter(c => c.required === "conditional").length})</TabsTrigger>
                  <TabsTrigger value="financial">Financial ({IMPORT_COLUMN_SPECS.filter(c => c.group === "financial").length})</TabsTrigger>
                  <TabsTrigger value="metadata">Metadata ({IMPORT_COLUMN_SPECS.filter(c => c.group === "metadata").length})</TabsTrigger>
                </TabsList>
              </Tabs>

              {/* Column Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredSpecs.map((col: ColumnSpec) => (
                  <div key={col.key} className="p-4 rounded-lg border bg-card/60 space-y-2 hover:border-primary/40 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="font-semibold text-sm text-foreground">{col.label}</span>
                        <code className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground font-mono">
                          {col.key}
                        </code>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        {col.required === "required" && (
                          <Badge variant="destructive" className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5">
                            Required
                          </Badge>
                        )}
                        {col.required === "conditional" && (
                          <Badge className="bg-amber-500 hover:bg-amber-600 text-white text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5">
                            Conditional
                          </Badge>
                        )}
                        {col.required === "optional" && (
                          <Badge variant="outline" className="text-[10px] uppercase tracking-wider px-1.5 py-0.5">
                            Optional
                          </Badge>
                        )}
                      </div>
                    </div>

                    <p className="text-xs text-muted-foreground leading-relaxed">{col.description}</p>

                    {/* Predefined Dropdown Options Display */}
                    {col.options && col.options.length > 0 && (
                      <div className="pt-1.5 space-y-1">
                        <div className="text-[11px] font-semibold text-foreground flex items-center gap-1">
                          <HelpCircle className="h-3 w-3 text-primary" /> Predefined Dropdown Values (Select from list):
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {col.options.map((opt) => (
                            <Badge key={opt} variant="secondary" className="text-xs font-medium bg-primary/10 text-primary border-primary/20">
                              {opt}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* System Master Lookup Hint */}
                    {col.type === "lookup" && (
                      <div className="pt-1.5">
                        <div className="text-[11px] font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40 px-2 py-1 rounded border border-blue-200 dark:border-blue-900/50 inline-block">
                          Must match an active item from system Master Data (See Sheet 3 in downloaded Excel)
                        </div>
                      </div>
                    )}

                    <div className="text-[11px] text-muted-foreground pt-1 border-t border-border/50">
                      <span className="font-medium text-foreground">Example Value:</span> <code className="text-xs text-primary font-mono">{col.example}</code>
                    </div>
                  </div>
                ))}
              </div>

              {filteredSpecs.length === 0 && (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  No columns match your search string. Try clearing filters.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 2 — Upload & Validate */}
      {step === 2 && (
        <Card className="space-y-4 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Step 2 — Upload &amp; Validate Asset Data</h2>
              <p className="text-xs text-muted-foreground">Upload your completed .xlsx or .csv file for automatic field mapping and validation.</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setStep(1)}>
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Back to Guidelines
            </Button>
          </div>

          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
            onClick={() => fileInputRef.current?.click()}
            className="cursor-pointer rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 p-10 text-center hover:bg-primary/10 transition-colors"
          >
            <Upload className="mx-auto h-10 w-10 text-primary" />
            <p className="mt-3 text-base font-semibold">Drop your Excel (.xlsx) or CSV file here, or click to browse</p>
            <p className="text-xs text-muted-foreground mt-1">Accepts standard .xlsx, .xls, or .csv templates</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              hidden
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
          </div>

          {parsing && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm font-medium text-primary">
              <Loader2 className="h-5 w-5 animate-spin" /> Parsing spreadsheet headers and validating master lookups…
            </div>
          )}

          {validated.length > 0 && (
            <div className="space-y-4 pt-2">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b pb-3">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary" className="px-3 py-1 text-xs">Total Rows: {validated.length}</Badge>
                  <Badge className="bg-emerald-600 text-white px-3 py-1 text-xs">Valid Assets: {validRows.length}</Badge>
                  <Badge variant={errorRows.length > 0 ? "destructive" : "outline"} className="px-3 py-1 text-xs">
                    Errors Found: {errorRows.length}
                  </Badge>
                </div>

                {errorRows.length > 0 && (
                  <Button size="sm" variant="outline" onClick={() => downloadErrorReport(validated)}>
                    <Download className="mr-1.5 h-4 w-4" /> Download Error Report (.csv)
                  </Button>
                )}
              </div>

              {errorRows.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-destructive flex items-center gap-1.5">
                    <AlertCircle className="h-4 w-4" /> Rows with Errors (Will be skipped during import)
                  </h3>
                  <div className="max-h-72 overflow-auto rounded-lg border">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="w-16">Row #</TableHead>
                          <TableHead className="w-48">Asset Name</TableHead>
                          <TableHead>Validation Error Details</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {errorRows.map((v) => (
                          <TableRow key={v.rowIndex}>
                            <TableCell className="font-mono text-xs font-semibold">{v.rowIndex}</TableCell>
                            <TableCell className="font-medium text-xs">{v.raw.asset_name || "—"}</TableCell>
                            <TableCell className="text-xs text-destructive font-medium">{v.errors.join("; ")}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button size="lg" disabled={validRows.length === 0} onClick={() => setStep(3)}>
                  Proceed to Review {validRows.length} Valid Assets <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Step 3 — Review & Confirm */}
      {step === 3 && (
        <Card className="space-y-5 p-6 shadow-sm">
          <div className="flex items-center justify-between border-b pb-3">
            <div>
              <h2 className="text-lg font-semibold">Step 3 — Review Valid Assets &amp; Confirm Import</h2>
              <p className="text-xs text-muted-foreground">Verify payload data before generating serialized asset records in HAMS.</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setStep(2)} disabled={importing || !!summary}>
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Back to Upload
            </Button>
          </div>

          {!summary ? (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Showing preview of first 10 valid asset records. {errorRows.length > 0 && `${errorRows.length} rows with errors will be skipped.`}
              </p>
              <div className="max-h-80 overflow-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-14">Row</TableHead>
                      <TableHead>Asset Name</TableHead>
                      <TableHead>Asset Class</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Basic Cost (₹)</TableHead>
                      <TableHead>Store Target</TableHead>
                      <TableHead>Location Target</TableHead>
                      <TableHead>Department</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {validRows.slice(0, 10).map((v) => (
                      <TableRow key={v.rowIndex}>
                        <TableCell className="font-mono text-xs font-semibold">{v.rowIndex}</TableCell>
                        <TableCell className="font-medium text-xs">{v.raw.asset_name}</TableCell>
                        <TableCell><Badge variant="secondary" className="text-[11px]">{v.raw.asset_class}</Badge></TableCell>
                        <TableCell className="text-xs">{v.raw.parent_category_name}</TableCell>
                        <TableCell className="text-xs font-mono tabular-nums">₹{v.raw.basic_cost ?? "—"}</TableCell>
                        <TableCell className="text-muted-foreground text-xs">{v.raw.store_name ?? "—"}</TableCell>
                        <TableCell className="text-muted-foreground text-xs">{v.raw.location_name ?? "—"}</TableCell>
                        <TableCell className="text-muted-foreground text-xs">{v.raw.department_name ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end pt-2">
                <Button size="lg" onClick={runImport} disabled={importing || validRows.length === 0}>
                  {importing ? (
                    <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Creating Asset Records…</>
                  ) : (
                    `Confirm & Import ${validRows.length} Assets`
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-50 p-4 text-sm text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200">
                <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
                <div>
                  <div className="font-bold text-base">Import Completed Successfully</div>
                  <div className="text-xs mt-0.5">
                    <strong>{summary.imported.length}</strong> new asset records generated. <strong>{summary.failed + errorRows.length}</strong> rows skipped.
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                {summary.imported.length > 0 && (
                  <Button variant="outline" onClick={() => downloadCodesList(summary.imported)}>
                    <Download className="mr-2 h-4 w-4" /> Download Generated Asset Codes (.csv)
                  </Button>
                )}
                <Button onClick={() => navigate({ to: "/assets" })}>Go to Asset Registry</Button>
              </div>

              {summary.imported.length > 0 && (
                <div className="space-y-2 pt-2">
                  <div className="text-xs font-semibold text-foreground">Generated Asset Codes ({summary.imported.length}):</div>
                  <div className="max-h-48 overflow-auto rounded-lg border bg-muted/40 p-3 text-xs font-mono grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                    {summary.imported.map((code) => (
                      <span key={code} className="bg-card px-2 py-1 rounded border text-center font-medium">
                        {code}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
